import React from "react";
import Counter from "../Counter";
import Reveal from "../Reveal";
import SectionIntro from "./SectionIntro";
import useParallax from "../../hooks/useParallax";
import { IMPACT } from "../../data/about";

/**
 * The figures, set as type rather than tiled as cards.
 *
 * Two numbers carry the section at display size and are placed against each
 * other rather than beside each other — one high and left, one low and right,
 * with the rest of the inventory running along the base as a rule-separated
 * index. The asymmetry is doing the work a row of four equal boxes usually
 * fails to do: telling the reader which figures actually matter.
 */

function Primary({ stat, align }) {
  const drift = useParallax({ speed: align === "right" ? 30 : -30 });

  return (
    <div
      ref={drift}
      className={`mw-parallax ${align === "right" ? "lg:text-right" : ""}`}
    >
      <span className="mw-display block bg-[linear-gradient(110deg,#FFFFFF_0%,#E9D5FF_46%,#C084FC_100%)] bg-clip-text text-transparent">
        <Counter value={stat.value} display={stat.display} suffix={stat.suffix} />
      </span>

      <div
        className={`mt-5 flex items-center gap-4 ${
          align === "right" ? "lg:justify-end" : ""
        }`}
      >
        <span className="h-px w-10 bg-[#FBCFE8]/50" aria-hidden="true" />
        <span className="text-lg font-semibold text-white">{stat.label}</span>
      </div>
      <p className="mw-eyebrow mt-3 text-[#8E76AE]">{stat.note}</p>
    </div>
  );
}

function ImpactMetrics() {
  return (
    <section
      id="impact"
      className="relative isolate scroll-mt-24 overflow-hidden bg-[#0B0518] py-24 lg:py-32"
    >
      <div className="absolute inset-0" aria-hidden="true">
        <div className="mw-aurora absolute -top-40 left-1/3 h-[38rem] w-[38rem] rounded-full bg-[#6A0DAD]/25 blur-[140px]" />
        <div className="mw-grid-dark absolute inset-0" />
        <div className="mw-grain absolute inset-0 opacity-[0.12] mix-blend-overlay" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <Reveal>
          <SectionIntro
            index={IMPACT.index}
            label={IMPACT.label}
            title={IMPACT.title}
            tone="dark"
          />
        </Reveal>

        {/* The two figures that matter, deliberately not level with each
            other. The second is stepped down on wide screens so the pair
            reads as a diagonal rather than as a row. */}
        <div className="mt-16 grid grid-cols-1 gap-14 lg:mt-20 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6">
            <Reveal>
              <Primary stat={IMPACT.primary[0]} align="left" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:mt-24">
            <Reveal delay={120}>
              <Primary stat={IMPACT.primary[1]} align="right" />
            </Reveal>
          </div>
        </div>

        {/* The rest of the inventory, as an index along the base. */}
        <div className="mt-20 grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:mt-28 lg:grid-cols-6">
          {IMPACT.secondary.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 70}>
              <div className="border-t border-white/15 pt-5">
                <span className="block text-[1.9rem] font-bold leading-none tracking-tight text-white sm:text-[2.2rem]">
                  <Counter
                    value={stat.value}
                    display={stat.display}
                    suffix={stat.suffix}
                  />
                </span>
                <span className="mw-eyebrow mt-3 block text-[#8E76AE]">
                  {stat.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ImpactMetrics;
